import React from "react";
import { Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import { toast } from "sonner";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import AddFactDialog, { FactSection } from "./AddFactDialog";
import FactEditDialog from "./FactEditDialog";
import { createFactsColumns } from "./factColumns";
import { useFactsTable } from "../hooks/useFactsTable";
import { Fact } from "../types";
import { deleteFact } from "../api/factsApi";

export interface FactManagementPageProps {
  section: FactSection;
  type: string;
  title: string;
  description?: string;
  canDelete?: boolean;
  addButtonLabel?: string;
}

export default function FactManagementPage({
  section,
  type,
  title,
  description,
  canDelete = true,
  addButtonLabel = "Add Fact",
}: FactManagementPageProps) {
  const [addOpen, setAddOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [editingFact, setEditingFact] = React.useState<any | null>(null);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const confirmActionRef = React.useRef<() => void>(() => {});

  const queryClient = useQueryClient();
  const { facts, queryKey, isLoading, findFactById } = useFactsTable({ type });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteFact(id),
    onMutate: () => {
      toast.loading("Deleting fact...");
    },
    onSuccess: () => {
      toast.dismiss();
      toast.success("Fact deleted");
      queryClient.invalidateQueries({ queryKey });
      setConfirmOpen(false);
    },
    onError: () => {
      toast.dismiss();
      toast.error("Failed to delete fact");
      setConfirmOpen(false);
    },
  });

  const handleEdit = React.useCallback(
    (fact: Fact) => {
      const fullFact = findFactById(fact.id) ?? fact;
      setEditingFact(fullFact);
      setEditOpen(true);
    },
    [findFactById]
  );

  const handleDelete = React.useCallback(
    (fact: Fact) => {
      confirmActionRef.current = () => {
        setConfirmOpen(false);
        deleteMutation.mutate(fact.id);
      };
      setConfirmOpen(true);
    },
    [deleteMutation]
  );

  const columns = React.useMemo(() => createFactsColumns({ onEdit: handleEdit, onDelete: handleDelete, canDelete }), [canDelete, handleDelete, handleEdit]);

  return (
    <div className="space-y-4">
      <h1 className="text-3xl">{title}</h1>
      {description ? <p>{description}</p> : null}
      <div className="flex justify-start">
        <Button size="sm" onClick={() => setAddOpen(true)} className="flex items-center gap-2">
          <Plus size={16} /> {addButtonLabel}
        </Button>
      </div>
      {isLoading ? (
        <div className="flex justify-center items-center h-32">
          <Loader2 className="animate-spin w-6 h-6" />
        </div>
      ) : (
        <DataTable columns={columns} data={facts} placeholder="Filter Facts" filterColumns={["title"]} />
      )}
      <AddFactDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        section={section}
        onCreated={() => {
          queryClient.invalidateQueries({ queryKey });
        }}
      />
      <FactEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        fact={editingFact}
        onSaved={() => {
          queryClient.invalidateQueries({ queryKey });
        }}
      />
      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        actionButtonRef={confirmActionRef}
        isLoading={deleteMutation.isPending}
        AlertdialogDescription="Are you sure you want to delete this fact? This action cannot be undone."
      />
    </div>
  );
}
